import { z } from "zod";
import { UuidSchema } from "./common.js";
import { Model3dCalibrationSchema } from "./models-3d.js";

// Spec 078 — o read model da cena (D-7: UM endpoint devolve tudo; o cliente não compõe nada).
// O mesmo payload alimenta a tela da linha, a rota ambiente (sessão ou link de exibição) e o
// painel textual acessível. Contrato completo em contracts/scene-read-model.md.

export const ScenePlacementSourceSchema = z.enum(["machine", "canvas", "fallback"]);
export type ScenePlacementSource = z.infer<typeof ScenePlacementSourceSchema>;

export const ScenePlacementSchema = z.object({
  x: z.number().finite(),
  z: z.number().finite(),
  rotationDeg: z.number().finite(),
  source: ScenePlacementSourceSchema,
});
export type ScenePlacement = z.infer<typeof ScenePlacementSchema>;

const measuredShape = {
  rateUnH: z.number().finite().nonnegative(),
  ratio: z.number().finite().nonnegative(),
  lastSampleAt: z.string().datetime({ offset: true }),
  sampleCount: z.number().int().nonnegative(),
  /**
   * Unidade do parâmetro que conta a vazão (kg, un, sc…). `null` ⇒ o parâmetro não declarou uma,
   * e a tela cai em "un". A cena escreve a unidade do DADO — dizer "un/h" sobre um contador em
   * quilos é afirmar uma grandeza que ninguém mediu.
   */
  unit: z.string().nullable(),
};

/**
 * As ausências têm nomes (FR-025): nenhuma colapsa noutra.
 * precedência no service: nodata > stopped > unassigned > série (measured/aged/insufficient).
 */
export const SceneFlowSchema = z.discriminatedUnion("quality", [
  z.object({ quality: z.literal("measured"), ...measuredShape }),
  z.object({ quality: z.literal("aged"), ...measuredShape }),
  z.object({
    quality: z.literal("insufficient"),
    lastSampleAt: z.string().datetime({ offset: true }).nullable(),
    sampleCount: z.number().int().nonnegative(),
  }),
  z.object({ quality: z.literal("unassigned") }),
  z.object({ quality: z.literal("nodata") }),
  z.object({ quality: z.literal("stopped") }),
]);
export type SceneFlow = z.infer<typeof SceneFlowSchema>;
export type SceneFlowQuality = SceneFlow["quality"];

export const SceneNominalSchema = z.object({
  rateUnH: z.number().positive(),
  /** `default` ⇒ rótulo "estimado" (SIM_DEFAULTS); `pair` ⇒ feedRateUnH do par máquina × produto. */
  provenance: z.enum(["pair", "default"]),
  /**
   * Unidade da REFERÊNCIA, e ela pode divergir da unidade da vazão — é o único aviso possível de
   * que a razão compara coisas diferentes. `provenance: "pair"` herda a unidade do parâmetro com
   * papel FLOW (o ritmo foi cadastrado para esta máquina × este produto, sobre a mesma coisa que
   * o contador conta); `provenance: "default"` é SEMPRE `null` — o `Un` de `feedRateUnH` é uma
   * contagem de peça fixa do motor, e carimbá-la com "kg" afirmaria uma grandeza que ninguém
   * mediu. `null` ⇒ a tela cai em "un".
   */
  unit: z.string().nullable(),
});
export type SceneNominal = z.infer<typeof SceneNominalSchema>;

/**
 * Spec 080 — o bloco de PARADA TÉRMICA. Saíram `value`, `unit`, `limitMax`, `limitMin` e
 * `ruleMissing`: um contato seco não tem valor, unidade nem faixa, e "papel sem limite de alerta"
 * deixou de ser um estado possível quando papel e limite pararam de se falar (FR-012).
 *
 * A âncora é o EVENTO de parada aberto, não o alarme (FR-009): o alarme só nasce com a linha em
 * produção ATIVA e `generatesAlarm` ligado, então prendê-lo ao alarme apagaria o alerta justamente
 * no disparo de madrugada e no disparo com a linha pausada — os casos em que a tela ambiente é a
 * única coisa olhando.
 */
export const SceneHeatSchema = z.object({
  parameterId: UuidSchema,
  parameterName: z.string(),
  /** Quando o sinal derrubou a máquina — `startAt` do evento de parada aberto. */
  trippedAt: z.string().datetime({ offset: true }),
  /** Alguém já reconheceu esta parada. Vem do EVENTO, não do alarme — é o que faz o pulso
   *  funcionar também quando não há alarme (parada fora de produção). */
  acknowledged: z.boolean(),
  /** Presente só quando a parada abriu alarme (produção ATIVA + geração ligada). Ausente NÃO
   *  significa "sem disparo": significa "sem alarme". */
  alarm: z
    .object({
      id: UuidSchema,
      state: z.enum(["UNACKNOWLEDGED", "ACKNOWLEDGED"]),
      startAt: z.string().datetime({ offset: true }),
    })
    .nullable(),
});
export type SceneHeat = z.infer<typeof SceneHeatSchema>;

export const SceneMachineSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  status: z.enum(["RUNNING", "STOPPED", "IDLE", "NODATA"]),
  statusSince: z.string().datetime({ offset: true }).nullable(),
  influencesStatus: z.boolean(),
  /** null ⇒ bloco genérico; id sem entrada em `models` ⇒ "Modelo indisponível". */
  model3dId: UuidSchema.nullable(),
  placement: ScenePlacementSchema,
  flow: SceneFlowSchema,
  nominal: SceneNominalSchema,
  /** null ⇒ a máquina não tem papel TEMPERATURE, OU tem e não há parada em aberto causada por ele. */
  heat: SceneHeatSchema.nullable(),
});
export type SceneMachine = z.infer<typeof SceneMachineSchema>;

export const SceneModelSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  /** RELATIVA: /api/v1/models-3d/:id/file?v=<hash> (ou a rota de display com o token). */
  fileUrl: z.string(),
  fileHash: z.string().length(64),
  calibration: Model3dCalibrationSchema,
});
export type SceneModel = z.infer<typeof SceneModelSchema>;

/**
 * Um trecho de material correndo: um por par máquina a montante → estágio seguinte (FR-022a).
 * As pontas são simétricas e ao menos uma é máquina:
 *   `fromMachineId: null` ⇒ ENTRADA da linha (nasce 3 m antes da boca de quem recebe)
 *   `toMachineId: null`   ⇒ SAÍDA da linha (morre 3 m depois da boca de quem entrega)
 * `drivenByMachineId` é sempre a ponta que existe: quem alimenta o trecho dita a velocidade.
 */
export const SceneSegmentSchema = z.object({
  id: z.string(),
  fromMachineId: UuidSchema.nullable(),
  toMachineId: UuidSchema.nullable(),
  drivenByMachineId: UuidSchema,
});
export type SceneSegment = z.infer<typeof SceneSegmentSchema>;

export const SceneMetaSchema = z.object({
  lineId: UuidSchema,
  lineName: z.string(),
  lineStatus: z.enum(["RUNNING", "STOPPED", "PARTIAL", "IDLE"]),
  generatedAt: z.string().datetime({ offset: true }),
  /** fallback ⇒ nenhuma máquina tem nó no canvas ⇒ a cena avisa "disposição provisória". */
  layoutSource: z.enum(["canvas", "fallback"]),
  access: z.enum(["session", "display"]),
  activeRun: z.object({ id: UuidSchema, productName: z.string().nullable() }).nullable(),
});
export type SceneMeta = z.infer<typeof SceneMetaSchema>;

export const LineSceneResponseSchema = z.object({
  meta: SceneMetaSchema,
  models: z.array(SceneModelSchema),
  machines: z.array(SceneMachineSchema),
  segments: z.array(SceneSegmentSchema),
});
export type LineSceneResponse = z.infer<typeof LineSceneResponseSchema>;

export const LineSceneEnvelopeSchema = z.object({
  data: LineSceneResponseSchema,
  error: z.null(),
});
